// FNP-969 / FNP-966: a TestRun is ONE instance of a TestDefinition executed by
// ONE synthetic user during ONE canary wake.
//
// ⚠️ THIS MODULE IS READ-ONLY BY DESIGN. DO NOT ADD CRUD.
// `openTestRun`, `closeTestRun` and `abandonTestRun` are all `@aws_iam` on the
// testing-api — they are written by the executor canary and the abandon sweep,
// never by a browser. A run is a RECORD OF SOMETHING THAT HAPPENED; a human
// cannot author one, so there is deliberately no Add drawer, no Edit form and no
// Delete button anywhere in this feature. If you find yourself wanting one, the
// thing you actually want is a new TestDefinition (testing-test-definitions).

export enum TestRunStatus {
    // Still in flight: the executor holds a lease and has not closed the run.
    RUNNING = 'RUNNING',
    PASSED = 'PASSED',
    // The test RAN and did not pass. This is a statement about the PRODUCT.
    FAILED = 'FAILED',
    // ⚠️ NOT A FAILURE. The canary died mid-wake and the lease expired, so the
    // abandon sweep closed the run out. This is a statement about the HARNESS,
    // not the product under test. Conflating it with FAILED reports platform
    // defects that never happened — keeping the two visually distinct is the
    // whole reason this module exists. See TestRunStatusBadge.
    ABANDONED = 'ABANDONED',
}

export type TestRun = {
    id: string,
    // FOREIGN KEY into the testing-api's `TestDefinition` (testing-test-definitions).
    testDefinitionId: string,
    // Denormalised label copied at open time, e.g. "portal.signup-journey".
    // Nullable: a run opened before the denormalisation landed has none, and the
    // definition it points at may since have been renamed. Displayed in
    // preference to the raw id because the id is an opaque uuid, but it is a
    // SNAPSHOT — it is not re-resolved.
    testCode?: string | null,
    personaId?: string | null,
    syntheticUserId: string,
    startedAt: string,
    // Absent while RUNNING — its absence is what "still in flight" looks like.
    completedAt?: string | null,
    status: TestRunStatus,
    stepsTotal?: number | null,
    stepsPassed?: number | null,
    stepsFailed?: number | null,
    stepsSkipped?: number | null,
    // REMOVED by the API when the run closes, so a value here on a closed run is
    // not meaningful. On a RUNNING run it is the deadline after which the sweep
    // will abandon it.
    leaseExpiresAt?: string | null,
    // Which canary run held the lease. For an ABANDONED run this is THE ONLY
    // EVIDENCE of which canary stranded it — surfaced on the detail view for
    // exactly that reason.
    claimedBy?: string | null,
    created?: string | null,
    updated?: string | null,
}

export type ListTestRuns = {
    listTestRuns: { items: TestRun[], nextToken?: string }
}

export type GetTestRun = {
    getTestRun: TestRun
}

export type ListTestRunsByDefinition = {
    listTestRunsByDefinition: { items: TestRun[], nextToken?: string }
}
